import React, {
  useCallback,
  useRef,
  useState,
  ReactElement,
  useEffect
} from 'react';
import BasicDatePicker from '@components/form/date-picker/BasicDatePicker';
import {
  Box,
  Card,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Typography,
  Tooltip,
  useTheme,
  Fab
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import InfoIcon from '@mui/icons-material/Info';
import {PulseLoader} from 'react-spinners';
// import Grid from '@mui/material/Unstable_Grid2';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import FormConstants from '@shared/constants/FormConstants';
import SelectInput from '@components/form/select-input/SelectInput';
import {FormControlDiv, SelectionListDiv} from './ModuleSearchFilterStyle';
import {useParams, useLocation, useNavigate} from 'react-router-dom';
import {
  getSearchResultsData,
  editPurgeAndLegalHold,
  addNewExternalAuditData,
  addNewUserData
} from '@shared/services/service-handlers/PrivateService';
import EncryptUtils from 'src/utils/EncryptUtils';
import ModuleSearchPage from '../modules-search-page/ModuleSearchPage';
import {
  BASE_MENU_ITEM,
  BASE_ROUTE,
  BASE_ROUTE_MODULE,
  PARAMS_KEY
} from 'src/routes/BaseRoute';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useSnackbar} from 'notistack';
import {emailValidator} from '@shared/utils/Validate';
import {useAppContext} from '@context/AppContext';
import FormProvider from 'src/minimal-components/hook-form/form-provider';
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFTextField
} from 'src/minimal-components/hook-form';
import {LoadingButton} from '@mui/lab';
import {Stack} from '@mui/system';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import moment from 'moment';
import RHFRadioGroup from '@components/hook-form/rhf-radio-group';
import SelectInputMulti from '@components/form/select-input-multi/SelectInputMulti';
import dayjs from 'dayjs';
import SelectInputMultiAutoComplete from '@components/form/select-input-multi/SelectInputMultiAutoComplete';
import SelectInputMultiAutoCompleteLimitTag from '@components/form/select-input-multi/SelectInputMultiAutoCompleteLimitTag';
import YearPicker from '@components/form/year-picker/YearPicker';
import DateRangeSelector from '@components/rcm-components/DateRangeSelector';
import ExcelJS from 'exceljs';
import AuthUtils from 'src/utils/AuthUtils';
import FilterBuilderView from '@components/form/filter-builder/FilterBuilderView';

interface ModuleSearchFilterProps {
  uiFieldsData: any;
  searchResults?: any;
  handleButtonSubmit: (data: any) => void;
  handleEmptyRequest?: any;
  module?: string;
  user_access_module?: any;
  // eslint-disable-next-line react/boolean-prop-naming
  all_fields_required?: boolean;
  isComponentLoading?: boolean;
  handleCancelButton?: () => void;
  handleImportButton?: () => void;
  setExpanded?: () => void;
  gridTemplateColumns?: any;
  setParentState?: any;
}
interface FilterStateProps {
  formError: string;
  uiFields: Array<any>;
  dropDownValues: Array<any>;
  dropdownLoader: boolean;
  selectionListLoader: boolean;
  patientForm: boolean;
  accessForm: boolean;
  userForm: boolean;
  selectionListData: any;
  legacyAppValue: string;
  selections: string[];
  selectSubModuleValue: string;
  searchLoader: boolean;
  moduleAcessSelected: string;
}

const ModuleSearchFilter = (
  props: ModuleSearchFilterProps
): React.ReactElement => {
  const {
    uiFieldsData,
    handleButtonSubmit,
    handleEmptyRequest,
    handleCancelButton,
    handleImportButton,
    isComponentLoading,
    setExpanded,
    user_access_module
  } = props;
  const theme = useTheme();

  const {enqueueSnackbar} = useSnackbar();
  const {fields = {}, all_fields_required: allFieldsRequired} = uiFieldsData;
  const {t} = useTranslation();
  const [fieldsValidation, setFieldsValidation] = useState<any>({});
  const [dateFields, setDateFields] = useState<any>({});
  const params: any = useParams();
  const location = useLocation();
  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);
  const navigate = useNavigate();
  const sourceSystemOptions = useRef();
  const [inputValues, setInputValues] = useState<any>([]);
  const [auditValue, setAuditValue] = useState<any>([]);
  // Add this just after other useState declarations
  const [filterBuilderData, setFilterBuilderData] = useState({
    filters: []
  });
  const [filters, setFilters] = useState<any>([]);
  const query = new URLSearchParams(location.search);
  const encrypted = query.get(PARAMS_KEY);
  const decryptedParams = EncryptUtils.getDecryptedParams(encrypted);

  const evaluateLegacyAndResultable = async (
    fields?: Record<string, any> | Array<{key: string; value: any}> | null,
    methods?: {getValues?: (name: string) => any | Promise<any>} | null
  ) => {
    // Normalize fields into an array
    const fieldsArray: Array<{key: string; value: any}> = Array.isArray(fields)
      ? (fields as Array<{key: string; value: any}>)
      : fields && typeof fields === 'object'
        ? Object.entries(fields as Record<string, any>).map(([key, value]) => ({
            key,
            value
          }))
        : [];

    // Sort by order
    fieldsArray.sort((a, b) => {
      const ao = a?.value?.order;
      const bo = b?.value?.order;
      if (ao === undefined && bo === undefined) return 0;
      if (ao === undefined) return 1;
      if (bo === undefined) return -1;
      return ao < bo ? -1 : ao > bo ? 1 : 0;
    });

    // 1) whether legacy_app field exists
    const hasLegacyKey = fieldsArray.some(
      f => f.key === FormConstants.INPUT_FIELD_NAMES.LEGACY_APP
    );

    // 2) resultable_item field
    const resultableField = fieldsArray.find(f => f.key === 'resultable_item');

    // 3) check if resultable_item depends on legacy_app
    const resultableDependsOnLegacy =
      !!resultableField &&
      Array.isArray(resultableField.value?.sql_data?.variables) &&
      resultableField.value.sql_data.variables.some(
        (v: any) => v.req_column === FormConstants.INPUT_FIELD_NAMES.LEGACY_APP
      );

    // async helper for getValues
    const safeGetAsync = async (name: string) => {
      if (methods?.getValues) {
        return await methods.getValues(name);
      }
      return undefined;
    };

    // 4) legacy_app form value
    const legacyValue = await safeGetAsync(
      FormConstants.INPUT_FIELD_NAMES.LEGACY_APP
    );

    const hasLegacyValue =
      legacyValue !== undefined &&
      legacyValue !== null &&
      !(Array.isArray(legacyValue) && legacyValue.length === 0) &&
      !(typeof legacyValue === 'string' && legacyValue.trim() === '');

    // 5) resultable_item value
    const resultableValue = await safeGetAsync('resultable_item');

    const hasResultableValue = Array.isArray(resultableValue)
      ? resultableValue.length > 0
      : !!resultableValue;

    // result
    return {
      hasLegacyKey,
      resultableField: resultableField ?? null,
      resultableDependsOnLegacy: !!resultableDependsOnLegacy,
      legacyValue: legacyValue ?? null,
      hasLegacyValue,
      resultableValue: resultableValue ?? null,
      hasResultableValue
    };
  };

  const isClinicalResultsSubmodule =
      params?.module_name === FormConstants.MODULE.CLINICAL &&
      params?.submodule_name === FormConstants.SUB_MODULE.RESULTS;

  useEffect(() => {
    if (params.module_name === FormConstants.MODULE.AUDIT_LOGS) {
      sessionStorage.removeItem('inputFieldsData');
    }
  }, [auditValue]);

  const [state, setState] = useState<FilterStateProps>({
    formError: '',
    uiFields: [],
    dropDownValues: [],
    userForm: false,
    accessForm: false,
    patientForm: false,
    dropdownLoader: false,
    selectionListData: [],
    legacyAppValue: '',
    selections: [],
    selectionListLoader: false,
    selectSubModuleValue: '',
    searchLoader: false,
    moduleAcessSelected: ''
  });
  const {
    formError,
    uiFields,
    dropDownValues,
    userForm,
    accessForm,
    patientForm,
    dropdownLoader,
    selectionListData,
    legacyAppValue,
    selections,
    selectionListLoader,
    selectSubModuleValue,
    searchLoader,
    moduleAcessSelected
  } = state;

  const methods = useForm({});

  const ISBHOPTIONS = [
    {value: 'True', label: 'Include restricted data access logs'},
    {value: 'False', label: 'Exclude restricted data access logs'}
  ];
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: {errors, isSubmitting}
  } = methods;

  const {
    appState: {applicationName}
  } = useAppContext();

  React.useEffect(() => {
    setState(prevState => ({
      ...prevState,
      formError: ''
    }));
  }, [state.dropDownValues]);
  React.useEffect(() => {
    if (props.module) {
      if (uiFields.length) {
        let obj: any = {};
        uiFields.forEach((item: any) => {
          if (item?.value?.type === 'text' || item?.value?.type === 'string') {
            obj = {...obj, [item?.key]: false};
          }
        });
        setFieldsValidation(obj);

        let dateObj: any = {};
        uiFields.forEach((item: any) => {
          if (item?.value?.type === 'date') {
            const {key} = item;
            dateObj = {...dateObj, [item?.key]: key};
          }
        });
        setDateFields(dateObj);
      }
    } else {
      navigate(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiFields]);

  const getDropDownValues = async (
    fieldsList: any,
    currentSourceSystemValue?: string
  ) => {
    const promisesArray: any = [];
    const keys: any = [];
    // check this
    for (const item of fieldsList) {
      if (item?.value?.sql_data?.sql) {
        const data: any = item?.value;
        data.source_system = currentSourceSystemValue || '';
        data.patient_id = decryptedParams?.record_id || '';
        const {legacyValue} = await evaluateLegacyAndResultable(
          fields,
          methods
        );
        if (legacyValue) {
          data.legacy_app = methods.getValues(
            FormConstants.INPUT_FIELD_NAMES.LEGACY_APP
          );
        }
        if (item?.key === 'legacy_app') {
          data.is_legacy_app_dropdown = true;
        }
        const encryptRequestData = EncryptUtils.encryptedData(data);
        promisesArray.push(
          getSearchResultsData(applicationName, params.module_name, {
            tk: encryptRequestData
          })
        );
        keys.push(item.key);
      }
    }

    return {promisesArray, keys};
  };

  const initialInit = async (
    isLoaderRequired?: boolean,
    name?: string,
    currentSourceSystemValue?: string
  ) => {
    if (!isClinicalResultsSubmodule) {
      reset();
    }
    if (name && currentSourceSystemValue) {
      setValue(name, currentSourceSystemValue);
    }
    if (fields && Object.keys(fields).length) {
      let fieldsArray: any = [];
      Object.entries(fields).forEach(item => {
        const obj = {
          key: item[0],
          value: item[1]
        };
        fieldsArray.push(obj);
      });
      // Sorting
      fieldsArray.sort((a: any, b: any) =>
        a.value.order > b.value.order ? 1 : -1
      );
      setState(prevState => ({
        ...prevState,
        uiFields: fieldsArray
      }));
      if (fieldsArray.length) {
        if (isLoaderRequired) {
          setState(prevState => ({
            ...prevState,
            dropdownLoader: true
          }));
        }

        if (
          params.module_name === FormConstants.MODULE.CLINICAL &&
          params.submodule_name === FormConstants.SUB_MODULE.RESULTS
        ) {
          const {resultableDependsOnLegacy, hasLegacyValue} =
            await evaluateLegacyAndResultable(fields, methods);
          if (resultableDependsOnLegacy && !hasLegacyValue) {
            fieldsArray = fieldsArray.filter(
              (f: {key: string; value: any}) => f.key !== 'resultable_item'
            );
          }
        }

        const {keys, promisesArray} = await getDropDownValues(
          fieldsArray,
          currentSourceSystemValue
        );

        await Promise.all(promisesArray).then(result => {
          const ddList: any = [];

          if (result.length > 0) {
            result.forEach((item: any, index: number) => {
              const optionsList: any = [];

              if (item?.data?.length > 0) {
                item.data.forEach((option: any) => {
                  Object.entries(option).forEach(([key, value]: any) => {
                    if (key !== 'id') {
                      // Added the if condition for external audit screen to show only review_type options in DD
                      const valueObj = {
                        label: option['id'] ? option['id'] : key,
                        value
                      };
                      optionsList.push(valueObj);
                    }
                  });
                });
              }

              const ddlObj = {
                [keys[index]]: optionsList
              };
              ddList.push(ddlObj);
              if (
                keys[index] === FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM
              ) {
                sourceSystemOptions.current = optionsList;
              }
            });
          }

          if (
            params.module_name === FormConstants.MODULE.PURGE_AND_LEGAL_HOLD ||
            params.module_name === FormConstants.MODULE.EXTERNAL_AUDIT ||
            params.module_name === FormConstants.MODULE.PHARMACY ||
            params.module_name === 'course_management'
          ) {
            let staticDDList: any = [];
            Object.keys(fields).forEach(item => {
              if (item) {
                const ddOptions =
                  fields?.[item]?.drop_down_options ||
                  fields?.[item]?.drop_down_options?.[0]?.report_type ||
                  [];
                staticDDList = [...staticDDList, ...ddOptions];
              }
            });
            setState((prevState: any) => ({
              ...prevState,
              dropDownValues: [...ddList, ...staticDDList],
              dropdownLoader: false
            }));
          } else {
            setState(prevState => ({
              ...prevState,
              dropDownValues: ddList,
              dropdownLoader: false
            }));
          }
        });
      }
    }

    if (
      params.module_name === FormConstants.MODULE.AUDIT_TRAILS &&
      fields?.report_type
    ) {
      setState((prevState: any) => ({
        ...prevState,
        dropDownValues: fields?.report_type?.drop_down_options
      }));
    }
    if (params.module_name === 'audit_logs' && fields?.report_type) {
      setState((prevState: any) => ({
        ...prevState
      }));
    }
  };

  useEffect(() => {
    if (!isClinicalResultsSubmodule) {
      reset();
    }
    if (params.module_name === 'audit_logs') {
      sessionStorage.removeItem('inputFieldsData');
      setInputValues([]);
      // eslint-disable-next-line no-use-before-define
      getDefaultValue('');
    }
  }, [uiFieldsData]);

  useEffect(() => {
    initialInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  const saveInsertData = async (encryptRequestData: any) => {
    setState((prevState: any) => ({
      ...prevState,
      searchLoader: true
    }));
    const res = await editPurgeAndLegalHold({
      tk: encryptRequestData
    });
    if (res) {
      enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
      setState((prevState: any) => ({
        ...prevState,
        searchLoader: false
      }));

      if (handleCancelButton) {
        handleCancelButton();
      }
    }
  };

  const handleInsertPurgeScreenData = async (data: any) => {
    const payload = data;
    payload.sql_data = uiFieldsData.search_results.sql_data;
    if (selections.length) {
      payload.selection_list = selections;
    }
    try {
      payload.action = FormConstants.PURGE_TERMS.PURGE_INSERT;
      const encryptRequestData = EncryptUtils.encryptedData(payload);
      if (
        uiFieldsData.selection_list_needed_modules.includes(
          selectSubModuleValue
        )
      ) {
        if (
          selectionListData &&
          selectionListData.length &&
          !selections?.length
        ) {
          enqueueSnackbar(
            t('PRIVATE.PURGE.NO_SELECTION_LIST_MSG', {ns: 'private'}),
            SnackBarConfig.getWarning()
          );
        } else {
          saveInsertData(encryptRequestData);
        }
      } else {
        saveInsertData(encryptRequestData);
      }
    } catch (error) {
      setState((prevState: any) => ({
        ...prevState,
        searchLoader: false
      }));
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleInsertLegalHoldScreenData = async (data: any) => {
    const payload = data;
    payload.sql_data = uiFieldsData.search_results.sql_data;
    payload.requested_date = data.requested_date ? data.requested_date : '';

    try {
      payload.action = FormConstants.PURGE_TERMS.LEGAL_HOLD_INSERT;
      const encryptRequestData = EncryptUtils.encryptedData(payload);

      setState((prevState: any) => ({
        ...prevState,
        searchLoader: true
      }));
      const res = await editPurgeAndLegalHold({tk: encryptRequestData});
      if (res) {
        enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
        setState((prevState: any) => ({
          ...prevState,
          searchLoader: false
        }));
        if (handleCancelButton && typeof handleCancelButton === 'function') {
          handleCancelButton();
        }
      }
    } catch (error) {
      setState((prevState: any) => ({
        ...prevState,
        searchLoader: false
      }));
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };

  const handleExternalAuditScreenData = async (data: any) => {
    const payload = data;
    dropDownValues[1].review_type.map((item: any) => {
      if (item.value === data.review_type) {
        payload.review_type_id = item.label;
      }
    });
    payload.sql_data = uiFieldsData.search_results.sql_data;
    try {
      const encryptRequestData = EncryptUtils.encryptedData(payload);

      setState((prevState: any) => ({
        ...prevState,
        searchLoader: true
      }));

      const res = await addNewExternalAuditData({tk: encryptRequestData});
      if (res) {
        enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
        setState((prevState: any) => ({
          ...prevState,
          searchLoader: false
        }));
        if (handleCancelButton && typeof handleCancelButton === 'function') {
          handleCancelButton();
        }
      }
      if (handleCancelButton && typeof handleCancelButton === 'function') {
        handleCancelButton();
      }
    } catch (error) {
      setState((prevState: any) => ({
        ...prevState,
        searchLoader: false
      }));
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
    }
  };
  const handleaddnewCourse = () => {
    // Add logic to handle adding a new course
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${FormConstants.MODULE.COURSE_CATALOG}/new`,
      {
        state: {}
      }
    );
  };

  const handleUserAccessData = async (data: any) => {
    const payload = data;

    if (!emailValidator(payload.email_id)) {
      setState(prevState => ({
        ...prevState,
        formError: t('PRIVATE.COMMON.INVALID_EMAIL', {ns: 'private'})
      }));
      return;
    }

    dropDownValues[0].organization.map((item: any) => {
      if (item.value === data.organization) {
        payload.organization_id = item.label;
      }
    });
    try {
      const encryptRequestData = EncryptUtils.encryptedData(payload);

      setState((prevState: any) => ({
        ...prevState,
        searchLoader: true
      }));

      const res = await addNewUserData(
        {tk: encryptRequestData},
        applicationName
      );
      if (res) {
        enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
        setState((prevState: any) => ({
          ...prevState,
          searchLoader: false
        }));
      }

      if (handleCancelButton && typeof handleCancelButton === 'function') {
        handleCancelButton();
      }
    } catch (error) {
      setState((prevState: any) => ({
        ...prevState,
        searchLoader: false
      }));
      if (error) {
        const errMessage = getErrorMessage(error);
        const {data: errData}: any = error;
        const {email} = errData;
        enqueueSnackbar(`${errMessage} ${email}`, SnackBarConfig.getError());
      }
    }
  };

  // Form Submit
  const onSubmit = async (fieldsData: any) => {
    const isEmptyValue = (val: any) => {
      if (val === undefined || val === null) return true;
      if (typeof val === 'string') return val.trim() === '';
      if (Array.isArray(val)) return val.length === 0;
      return false;
   };
    let isFormDirty = false;

    // AuthUtils.clearSelectedPatientAccountingCheckBoxData();
    // AuthUtils.clearPatientAccountingSelectedCheckBoxList();
    if (localStorage.getItem('Patient_encounter_number_fetched') === 'true') {
      AuthUtils.clearSelectedAdditionalCheckBoxData();
      AuthUtils.clearAdditionalSelectedCheckBoxList();
      localStorage.removeItem('Patient_encounter_number_fetched');
    }
    if (
      localStorage.getItem('account_number_id') ||
      localStorage.getItem('encounter_number_id')
    ) {
      localStorage.removeItem('account_number_id');
      localStorage.removeItem('encounter_number_id');
      localStorage.removeItem('account_number_Date_id');
      localStorage.removeItem('account_number_Legacy_App');
      localStorage.removeItem('account_number_Legacy_Mrn');
      localStorage.removeItem('encounterIsBH');
      localStorage.removeItem('account_number_fetched');
      localStorage.removeItem('account_number_date');
    }
    const storedInputFields = JSON.parse(
      sessionStorage.getItem('inputFieldsData') || '[]'
    );
    const data = {...fieldsData, ...storedInputFields[0]};
    // Add filter builder data if present in uiFieldsData
    if (uiFieldsData?.filter_builder) {
      data.filter_builder = {
        filters: filterBuilderData.filters
      };
    }
    for (const key in fieldsValidation) {
      if (key) {
        isFormDirty = isFormDirty || fieldsValidation[key];
      }
    }
    if (isFormDirty) return;

    let valueEmpty = true;
    for (const key in data) {
      if (key && key !== 'module') {
        valueEmpty =
          (data[key] === undefined || data[key] === null || data[key] === '') &&
          valueEmpty;
      }
    }

    valueEmpty = true;
    for (const key in data) {
      if (key && key !== 'module') {
        valueEmpty = isEmptyValue(data[key]) && valueEmpty;
      }
    }

    if (params.module_name === FormConstants.MODULE.AUDIT_LOGS) {
      valueEmpty = false;
    }

    let validDate: any = {};
    if (dateFields) {
      Object.entries(dateFields).forEach((itemDate: any, index: number) => {
        Object.entries(data).forEach((dataItem: any) => {
          if (dataItem[0] === itemDate[0] && dataItem[1] !== null) {
            // If the value is a Dayjs object that is invalid (e.g. from a cleared
            // date picker that emits an invalid Dayjs rather than null), treat it
            // as absent so it doesn't trigger a spurious "Invalid Date" error.
            const val = dataItem[1];
            const isDayjsInvalid =
              val &&
              typeof val === 'object' &&
              typeof val.isValid === 'function' &&
              !val.isValid();
            if (isDayjsInvalid) {
              // Nullify the cleared date in data so downstream logic sees it as empty
              data[dataItem[0]] = null;
            } else {
              validDate = {...validDate, [index]: val};
            }
          }
        });
      });
    }

    let atLeastOneFieldEmpty = false;
    if (params.module_name != FormConstants.MODULE.AUDIT_LOGS) {
      Object.entries(data).forEach((item: any) => {
        if (item[1] === '' || item[1] === null) {
          atLeastOneFieldEmpty = true;
        }
      });
      Object.entries(data).forEach(([key, val]: any) => {
        if (key !== 'module' && isEmptyValue(val)) {
          atLeastOneFieldEmpty = true;
        }
      });
    }
    // if (
    //   moduleAcessSelected &&
    //   params.module_name === FormConstants.MODULE.AUDIT_LOGS
    // ) {
    //   data.moduleSelected = moduleAcessSelected;
    // }
    let atleasttwoFieldsRequired = false;
    // the code must work only when the module is audit trails and the two fields are required when the legacy apps are selected
    if (
      params.module_name === FormConstants.MODULE.AUDIT_TRAILS &&
      data.legacy_app
    ) {
      let counted = true;
      for (const key in data) {
        if (key && key !== 'module' && key !== 'legacy_app') {
          counted =
            (data[key] === undefined ||
              data[key] === null ||
              data[key] === '') &&
            counted;
        }
      }
      if (counted) {
        atleasttwoFieldsRequired = true;
      }
    }

    if (allFieldsRequired) {
      if (atLeastOneFieldEmpty) {
        setState(prevState => ({
          ...prevState,
          formError: t('PRIVATE.COMMON.ALL_FIELDS_REQUIRED', {ns: 'private'})
        }));
        if (handleEmptyRequest) handleEmptyRequest();
      } else if (
        params.module_name === FormConstants.MODULE.PURGE_AND_LEGAL_HOLD
      ) {
        if (menuItem === FormConstants.MENU_ITEM.LEGAL_HOLD) {
          const requestedDate =
            data.requested_date != null ? new Date(data.requested_date) : '';

          const date =
            requestedDate && requestedDate.toLocaleDateString('en-US');
          data.requested_date = date;
          handleInsertLegalHoldScreenData(data);
        }
        if (menuItem === FormConstants.MENU_ITEM.PURGE) {
          handleInsertPurgeScreenData(data);
        }
      } else if (menuItem === FormConstants.MENU_ITEM.EXTERNAL_AUDIT) {
        const requestedDate =
          data.requested_date !== null ? new Date(data.requested_date) : '';

        const date = requestedDate && requestedDate.toLocaleDateString('en-US');
        data.requested_date = date;
        handleExternalAuditScreenData(data);
      } else if (menuItem === FormConstants.MENU_ITEM.USER_ACCESS) {
        handleUserAccessData(data);
      } else {
        setState(prevState => ({
          ...prevState,
          formError: ''
        }));
        handleButtonSubmit(data);
      }
    } else if (valueEmpty) {
      setState(prevState => ({
        ...prevState,
        formError: t('PRIVATE.COMMON.ENTER_SOME_VALUES', {ns: 'private'})
      }));
      if (handleEmptyRequest) handleEmptyRequest();
    } else if (
      validDate &&
      Object.keys(validDate).length &&
      validDate !== null
    ) {
      let isDataInvalid = false;
      Object.entries(validDate).forEach((dateValidation: any) => {
        const isValidDate = dateValidation[1].isValid();
        const isBelow1900 = dateValidation[1].year() < 1900;
        if (!isValidDate || isBelow1900) {
          isDataInvalid = true;
          setState(prevState => ({
            ...prevState,
            formError: t('PRIVATE.COMMON.INVALID_DATE', {ns: 'private'})
          }));
        }
      });
      if (!isDataInvalid) {
        const startdate = 'start_date';
        const enddate = 'end_date';
        const legacydob = 'legacy_dob';
        if (data?.[legacydob]) {
          const datevalueparsed = data[legacydob]?.format('M/D/YYYY');
          data[legacydob] = datevalueparsed;
        }
        let dateerror;
        if (data?.[startdate]) {
          const datevalueparsed = data[startdate]?.format('M/D/YYYY');
          data[startdate] = datevalueparsed;
          dateerror = true;
        }
        if (data?.[enddate]) {
          const datevalueparsed = data[enddate]?.format('M/D/YYYY');
          data[enddate] = `${datevalueparsed}`;
          dateerror = true;
        }
        if (data[startdate] === null || data[enddate] === null) {
          setState(prevState => ({
            ...prevState,
            formError: t('PRIVATE.COMMON.START_DATE_END_DATE_REQUIRED', {
              ns: 'private'
            })
          }));
        }
        if (data[startdate] && data[enddate]) {
          const startDateParsed = moment(data[startdate], 'M/D/YYYY HH:mm:ss'); // Assuming format is 'M/D/YYYY HH:mm:ss'
          const endDateParsed = moment(data[enddate], 'M/D/YYYY HH:mm:ss'); // Assuming format is 'M/D/YYYY HH:mm:ss'
          if (startDateParsed.isAfter(endDateParsed)) {
            setState(prevState => ({
              ...prevState,
              formError: t('Start date is later than end date.')
            }));
          dateerror = true;
          } else if (
            (menuItem === 'user-log' ||
              menuItem === FormConstants.MODULE.USER_REPORT_LOG ||
              menuItem === FormConstants.MODULE.PATIENT_REPORT_LOG) &&
            endDateParsed.diff(startDateParsed, 'days') > 5
          ) {
            setState(prevState => ({
              ...prevState,
              formError: t('Date range cannot exceed 5 days for this search.')
            }));
            dateerror = true;
          } else {
            dateerror = false;
          }
        }
        if (!dateerror) {
          handleButtonSubmit(data);
          setState(prevState => ({
            ...prevState,
            formError: ''
          }));
        }
      }
    } else if (atleasttwoFieldsRequired) {
      setState(prevState => ({
        ...prevState,
        formError: t('PRIVATE.COMMON.ANOTHER_FIELD_REQUIRED', {ns: 'private'})
      }));
      if (handleEmptyRequest) handleEmptyRequest();
    } else if (data?.email && !emailValidator(data?.email)) {
      setState(prevState => ({
        ...prevState,
        formError: t('PRIVATE.COMMON.EMAIL_VALIDATION', {ns: 'private'})
      }));
    } else if (fieldsData?.email && !emailValidator(fieldsData?.email)) {
      setState(prevState => ({
        ...prevState,
        formError: t('PRIVATE.COMMON.EMAIL_VALIDATION', {ns: 'private'})
      }));
    } else if (
      params.module_name === FormConstants.MODULE.CLINICAL &&
      params?.submodule_name === FormConstants.SUB_MODULE.RESULTS
    ) {
      const {hasLegacyValue, hasResultableValue} =
        await evaluateLegacyAndResultable(fields, methods);
      data.patient_id = decryptedParams?.record_id || '';

      if (hasLegacyValue && hasResultableValue) {
        handleButtonSubmit(data);
        setState(prevState => ({
          ...prevState,
          formError: ''
        }));
      } else if (!hasResultableValue) {
        setState(prevState => ({
          ...prevState,
          formError: 'Please select one or more items in "Resultable..."'
        }));
      }
    } else {
      handleButtonSubmit(data);
      setState(prevState => ({
        ...prevState,
        formError: ''
      }));
    }
  };

  React.useEffect(() => {
    const handleKeyDown = (event: {
      code: string;
      preventDefault: () => void;
    }): any => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        handleSubmit(onSubmit)();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleSubmit, onSubmit]);

  const onReset = useCallback(() => {
    reset();
    setFilters([]);
    if (props?.module === 'patient-accounting') {
      props?.setParentState((prevState: any) => {
        return {category: prevState.category};
      });
    }

    sessionStorage.removeItem('inputFieldsData');
    setInputValues([]);
    // eslint-disable-next-line no-use-before-define
    getDefaultValue('');

    if (
      params.module_name === FormConstants.SCREENS.FINANCE &&
      uiFieldsData.menu_item === FormConstants.REPORTS_SCREENS.REPORTS
    ) {
      reset();
      return;
    }

    if (
      (params.module_name === FormConstants.SCREENS.FINANCE &&
        uiFieldsData.menu_item === FormConstants.REPORTS_SCREENS.REPORTS) ||
      ((params.module_name === FormConstants.SCREENS.LIS ||
        params.module_name === FormConstants.SCREENS.RIS) &&
        uiFieldsData.name === FormConstants.GLOBAL_SEARCH.global_search_tab)
    ) {
      setState(prevState => ({
        ...prevState,
        formError: '',
        dropDownValues: [
          {
            source_system: sourceSystemOptions.current
          }
        ]
      }));
    }

    if (handleEmptyRequest) {
      handleEmptyRequest();
    }

    if (setExpanded) {
      setExpanded();
    }
    if (uiFieldsData.is_tabs_reset === true) {
      return;
    }
    if (params.module_name !== FormConstants.SCREENS.LIS && params.module_name !== FormConstants.MODULE.AUDIT_LOGS) {
      initialInit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reset,
    props,
    params.module_name,
    uiFieldsData,
    handleEmptyRequest,
    setExpanded,
    initialInit
  ]);

  // eslint-disable-next-line no-useless-escape
  const specialChars = /[`!@#$%^&*()_+\=\[\]{};':"\\|,.<>\/?~]/;
  // to keep the session data for the input field after going one step furter
  useEffect(() => {
    const storedInputFields = JSON.parse(
      sessionStorage.getItem('inputFieldsData') as string
    );
    if (menuItem !== null && menuItem !== undefined && menuItem !== '') {
      sessionStorage.removeItem('inputFieldsData');
      setInputValues([]);
      return;
    }

    if (
      Array.isArray(storedInputFields) &&
      storedInputFields.length > 0 &&
      params.module_name !== storedInputFields[0].module
    ) {
      // Reset stored values if the module_name has changed
      sessionStorage.removeItem('inputFieldsData');
      setInputValues([]); // Reset inputValues state
    }
  }, [params.module_name, menuItem, props?.module]);

  const handleInputChange = (e: any) => {
    const {name, value} = e.target;
    const updatedValues = {
      ...inputValues[0],
      module: props?.module,
      [name]: value
    };
    setInputValues([updatedValues]);
    sessionStorage.setItem('inputFieldsData', JSON.stringify([updatedValues]));
  };

  const getDefaultValue = (inputName: string) => {
    const storedInputFields = JSON.parse(
      sessionStorage.getItem('inputFieldsData') as string
    );
    if (Array.isArray(storedInputFields) && storedInputFields.length > 0) {
      const defaultValueObj = storedInputFields.find(data => data[inputName]);
      const defaultValue = defaultValueObj ? defaultValueObj[inputName] : '';
      return defaultValue !== undefined ? defaultValue : '';
    } else {
      return ''; // Return an empty string if x is not an array or is empty
    }
  };
  const renderRadioButtonField = (
    inputName: string,
    item: any
  ): ReactElement => {
    return (
      // <Controller
      //   name={inputName}
      //   control={control}
      //   render={({field, fieldState: {error}}) => (
      //     <FormControl component="fieldset" error={!!errors[inputName]}>
      //       <FormLabel component="legend">
      //         {item.heading?.[0] || item.heading?.[1] || ''}
      //       </FormLabel>
      //       <RadioGroup
      //         aria-label="bhStatus"
      //         name="bhStatus"
      //         row // This prop aligns the radio buttons horizontally
      //         onChange={event => {
      //           const newValue = event.target.value;
      //           setState(prevState => ({
      //             ...prevState,
      //             bhStatus: newValue,
      //             formError: ''
      //           }));
      //         }}>
      //         <FormControlLabel
      //           value="isBhEnabled" // Value for "isBhEnabled"
      //           control={<Radio />}
      //           label="Include restricted data access logs" // Static label for enabled option
      //         />
      //         <FormControlLabel
      //           value="isBhDisabled" // Value for "isBhDisabled"
      //           control={<Radio />}
      //           label=" Exclude restricted data access logs" // Static label for disabled option
      //         />
      //       </RadioGroup>
      //       {errors[inputName] && (
      //         <FormHelperText>This field is required.</FormHelperText>
      //       )}
      //     </FormControl>
      //   )}
      // />
      <RHFRadioGroup row spacing={1} name={inputName} options={ISBHOPTIONS} />
    );
  };
  const renderCheckBoxButtonField = (
    inputName: string,
    item: any
  ): ReactElement => {
    return (
      // <Controller
      //   name={inputName}
      //   control={control}
      //   render={({field, fieldState: {error}}) => (
      //     <FormControl component="fieldset" error={!!errors[inputName]}>
      //       <FormLabel component="legend">
      //         {item.heading?.[0] || item.heading?.[1] || ''}
      //       </FormLabel>
      //       <RadioGroup
      //         aria-label="bhStatus"
      //         name="bhStatus"
      //         row // This prop aligns the radio buttons horizontally
      //         onChange={event => {
      //           const newValue = event.target.value;
      //           setState(prevState => ({
      //             ...prevState,
      //             bhStatus: newValue,
      //             formError: ''
      //           }));
      //         }}>
      //         <FormControlLabel
      //           value="isBhEnabled" // Value for "isBhEnabled"
      //           control={<Radio />}
      //           label="Include restricted data access logs" // Static label for enabled option
      //         />
      //         <FormControlLabel
      //           value="isBhDisabled" // Value for "isBhDisabled"
      //           control={<Radio />}
      //           label=" Exclude restricted data access logs" // Static label for disabled option
      //         />
      //       </RadioGroup>
      //       {errors[inputName] && (
      //         <FormHelperText>This field is required.</FormHelperText>
      //       )}
      //     </FormControl>
      //   )}
      // />
      <RHFCheckbox name={inputName} label={item.label} sx={{my: '5px'}} />
    );
  };
  const renderTextField = (inputName: string, item: any): ReactElement => {
    return (
      <RHFTextField
        name={inputName}
        type={item.type}
        label={
          item?.heading?.[0] ? item?.heading?.[0] || item?.heading?.[1] : ''
        }
        defaultValue={
          getDefaultValue(inputName) !== '' ? getDefaultValue(inputName) : ''
        }
        value={
          getDefaultValue(inputName) !== '' ? getDefaultValue(inputName) : ''
        }
        error={!!fieldsValidation[inputName]}
        helperText={
          fieldsValidation?.[inputName]
            ? t('PRIVATE.COMMON.SPECIAL_CHARACTERS_NOT_ALLOWED', {
                ns: 'private'
              })
            : ''
        }
        fullWidth
        placeholder={item?.placeholder?.[0] ? item?.placeholder?.[0] : ''}
        disabled={!!item.disabled}
        handleChange={(e: any) => {
          setFieldsValidation((prevState: any) => ({
            ...prevState,
            [inputName]:
              props.module === FormConstants.MODULE.EXTERNAL_AUDIT &&
              inputName != 'patient_list'
                ? specialChars.test(e?.target?.value)
                : ''
          }));
          setState(prevState => ({
            ...prevState,
            formError: ''
          }));
          handleInputChange(e);
        }}
      />
    );
  };
  const renderPrepostTextfield = (
    inputName: string,
    item: any
  ): ReactElement => {
    return (
      <Grid md={inputName === 'Description' ? 12 : 5.92}>
        <div style={{display: 'flex', paddingBottom: '1.5vh'}}>
          <h4 style={{paddingRight: '1vh'}}>
            {item?.heading?.[0] ? item?.heading?.[0] || item?.heading?.[1] : ''}
          </h4>
          {uiFieldsData.label === 'Basics' && (
            <div>
              <Tooltip
                title={
                  item?.display?.[0]
                    ? item?.display?.[0] || item?.display?.[1]
                    : ''
                }>
                <InfoIcon
                  style={{width: '0.6em', height: '0.6em'}}
                  color="action"
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div>
          <RHFTextField
            name={inputName}
            type={item.type}
            label={
              item?.heading?.[0] ? item?.heading?.[0] || item?.heading?.[1] : ''
            }
            defaultValue={
              getDefaultValue(inputName) !== ''
                ? getDefaultValue(inputName)
                : ''
            }
            value={
              getDefaultValue(inputName) !== ''
                ? getDefaultValue(inputName)
                : ''
            }
            error={!!fieldsValidation[inputName]}
            helperText={
              fieldsValidation?.[inputName]
                ? t('PRIVATE.COMMON.SPECIAL_CHARACTERS_NOT_ALLOWED', {
                    ns: 'private'
                  })
                : ''
            }
            fullWidth
            placeholder={item?.placeholder?.[0] ? item?.placeholder?.[0] : ''}
            disabled={!!item.disabled}
            handleChange={(e: any) => {
              setFieldsValidation((prevState: any) => ({
                ...prevState,
                [inputName]:
                  props.module === FormConstants.MODULE.EXTERNAL_AUDIT &&
                  inputName != 'patient_list'
                    ? specialChars.test(e?.target?.value)
                    : ''
              }));
              setState(prevState => ({
                ...prevState,
                formError: ''
              }));
              handleInputChange(e);
            }}
          />
        </div>
      </Grid>
    );
  };
  const renderDateField = (inputName: string, item: any): ReactElement => {
    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={null}
        render={({field}) => (
          <BasicDatePicker
            yearFormatView={item?.yearFormatView ? item?.yearFormatView : false}
            label={
              item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
            }
            error={!!errors[inputName]}
            helperText={
              `${item.heading?.[0]}` +
              t('PRIVATE.COMMON.IS_REQUIRED', {ns: 'private'})
            }
            field={field}
            handleChange={() => {
              setState(prevState => ({
                ...prevState,
                formError: ''
              }));
            }}
          />
        )}
      />
    );
  };
  const renderYearField = (inputName: string, item: any): ReactElement => {
    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={null}
        render={({field}) => (
          <YearPicker
            label={
              item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
            }
            error={!!errors[inputName]}
            helperText={
              `${item.heading?.[0]}` +
              t('PRIVATE.COMMON.IS_REQUIRED', {ns: 'private'})
            }
            field={field}
            handleChange={() => {
              setState(prevState => ({
                ...prevState,
                formError: ''
              }));
            }}
          />
        )}
      />
    );
  };

  const getDropdownsListByInputName = (key: string) => {
    const index = dropDownValues.findIndex((item: any) => {
      return Object.keys(item)?.[0] === key;
    });
    if (index > -1) {
      const selectedKey = Object.keys(dropDownValues[index])[0];
      return dropDownValues[index]?.[selectedKey];
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // show the respective search form based selected value
    const getLabelFromValue = (valueToFind: string) => {
      // eslint-disable-next-line camelcase
      const foundItem = user_access_module.find(
        (item: {value: string}) => item.value === valueToFind
      );
      return foundItem ? foundItem.label : null;
    };

    // Check if event.target.value is valid
    const isValidValue = (value: string) => {
      // eslint-disable-next-line camelcase
      const values = getLabelFromValue(value);
      if (!values) {
        return false;
      }
      return user_access_module.some(
        (item: {value: string}) => item.value === value
      );
    };

    if (event.target.value === FormConstants.STATIC_VALUES.USER) {
      setState((prevState: FilterStateProps) => ({
        ...prevState,
        userForm: true,
        patientForm: false
      }));
      navigate(
        `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${FormConstants.MODULE.USER_REPORT_LOG}`,
        {
          state: {}
        }
      );
    } else if (event.target.value === FormConstants.STATIC_VALUES.PATIENT) {
      setState((prevState: any) => ({
        ...prevState,
        patientForm: true,
        userForm: false
      }));
      navigate(
        `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${FormConstants.MODULE.PATIENT_REPORT_LOG}`,
        {
          state: {}
        }
      );
    } else if (
      props?.module === 'audit_logs' &&
      user_access_module &&
      isValidValue(event.target.value)
    ) {
      const labelForSelectedValue = getLabelFromValue(event.target.value);
      setState((prevState: any) => ({
        ...prevState,
        patientForm: false,
        userForm: false,
        accessForm: true,
        moduleAcessSelected: labelForSelectedValue
      }));
      navigate(
        `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${FormConstants.MODULE.USER_ACCESS_LOGS}`,
        {
          state: {}
        }
      );
    }
  };

  const handleDropdownChange = async (event: any, name: string) => {
    if (name === FormConstants.SUB_MODULE.SUB_MODULE) {
      setState((prevState: FilterStateProps) => ({
        ...prevState,
        selections: []
      }));
    }
    handleChange(event);
    if (name === FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM) {
      await initialInit(false, name, event?.target?.value);
      setValue(
        FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM,
        event?.target?.value
      );
    }
    const {resultableDependsOnLegacy, hasLegacyValue} =
      await evaluateLegacyAndResultable(fields, methods);

    if (
      name === FormConstants.INPUT_FIELD_NAMES.LEGACY_APP &&
      resultableDependsOnLegacy &&
      hasLegacyValue &&
      params.module_name === FormConstants.MODULE.CLINICAL &&
      params.submodule_name === FormConstants.SUB_MODULE.RESULTS
    ) {
      setValue('resultable_item', []);
      await initialInit(false, name, event?.target?.value);
      setValue(
        FormConstants.INPUT_FIELD_NAMES.LEGACY_APP,
        event?.target?.value
      );
    }

    if (
      params.module_name === FormConstants.MODULE.PURGE_AND_LEGAL_HOLD &&
      name === FormConstants.INPUT_FIELD_NAMES.LEGACY_APP
    ) {
      setState((prevState: FilterStateProps) => ({
        ...prevState,
        legacyAppValue: event?.target?.value
      }));
      if (uiFieldsData.tab_name === FormConstants.PURGE_TERMS.PURGE) {
        setValue('sub_module', '');
      }
    }
  };

  const handleSelectionList = (event: any) => {
    setState((prevState: FilterStateProps) => ({
      ...prevState,
      selectSubModuleValue: event.target.value
    }));
    if (
      uiFieldsData.selection_list_needed_modules.includes(event.target.value)
    ) {
      if (legacyAppValue === '') {
        enqueueSnackbar(
          t('PRIVATE.PURGE.SELECT_LEGACY_APP_VALUE', {ns: 'private'}),
          SnackBarConfig.getWarning()
        );
      } else {
        uiFields[1].value.drop_down_options[0].sub_module.map(
          async (item: any) => {
            let selectionList: any = {};
            try {
              if (item.selection_list_needed) {
                if (item.value === event.target.value) {
                  const data = item;
                  data.legacy_app = legacyAppValue || '';
                  const encryptRequestData = EncryptUtils.encryptedData(data);

                  setState((prevState: FilterStateProps) => ({
                    ...prevState,
                    selectionListLoader: true
                  }));

                  selectionList = await getSearchResultsData(
                    applicationName,
                    params.module_name,
                    {
                      tk: encryptRequestData
                    }
                  );
                }
                setState((prevState: FilterStateProps) => ({
                  ...prevState,
                  selectionListData: selectionList.data || [],
                  selectionListLoader: false
                }));
              }
            } catch (error) {
              const errMessage = getErrorMessage(error);
              enqueueSnackbar(errMessage, SnackBarConfig.getError());
            }
          }
        );
      }
    }
  };

  const renderSelectFieldsMultiSearchable = (
    inputName: string,
    item: any
  ): ReactElement => {
    let optionsList: any = [];
    if (item.module_passed_for_dropdown && user_access_module.length > 0) {
      optionsList = user_access_module;
    } else {
      optionsList = item.static_dropdown_value
        ? item.static_dropdown_values
        : getDropdownsListByInputName(inputName) ||
          item?.drop_down_options ||
          item?.drop_down_options?.[0]?.report_type ||
          [];
    }

    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={
          inputName === FormConstants.PURGE_TERMS.RETENTION_YEARS ||
          inputName === FormConstants.PURGE_TERMS.RETENTION_MONTHS
            ? '0'
            : ''
        }
        render={({field: {value, onChange}}) => (
          <FormControlDiv>
            <SelectInputMultiAutoCompleteLimitTag
              label={
                item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
              }
              placeholder={item.placeholder}
              name={inputName}
              value={value || ''}
              options={optionsList}
              onChangeCallback={(value: any) => {
                setAuditValue({value});
                onChange(value);
                setState(prevState => ({
                  ...prevState,
                  formError: ''
                }));
                handleDropdownChange({target: {value}}, inputName);
                if (inputName === FormConstants.SUB_MODULE.SUB_MODULE) {
                  handleSelectionList({target: {value}});
                }
              }}
              optionValueKey={'value'}
              optionLabelKey={'value'}
            />
          </FormControlDiv>
        )}
      />
    );
  };
  const renderSelectFieldsMulti = (
    inputName: string,
    item: any
  ): ReactElement => {
    let optionsList: any = [];
    if (item.module_passed_for_dropdown && user_access_module.length > 0) {
      optionsList = user_access_module;
    } else {
      optionsList = item.static_dropdown_value
        ? item.static_dropdown_values
        : getDropdownsListByInputName(inputName) ||
          item?.drop_down_options ||
          item?.drop_down_options?.[0]?.report_type ||
          [];
    }

    return (
      <Controller
        name={inputName}
        control={control}
        defaultValue={
          inputName === FormConstants.PURGE_TERMS.RETENTION_YEARS ||
          inputName === FormConstants.PURGE_TERMS.RETENTION_MONTHS
            ? '0'
            : ''
        }
        render={({field: {value, onChange}}) => (
          <FormControlDiv>
            <SelectInputMulti
              selectProps={{multiple: true}}
              enableDefaultOption
              options={optionsList}
              label={
                item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
              }
              placeholder={item.placeholder}
              name={inputName}
              value={value || ''}
              fullWidth
              onChange={(event: any) => {
                setAuditValue(event.target);
                onChange(event);
                setState(prevState => ({
                  ...prevState,
                  formError: ''
                }));
                handleDropdownChange(event, inputName);
                if (inputName === FormConstants.SUB_MODULE.SUB_MODULE) {
                  handleSelectionList(event);
                }
              }}
              optionValueKey={'value'}
              optionLabelKey={'value'}
              disabled={
                params.module_name === FormConstants.SCREENS.FINANCE &&
                uiFieldsData.menu_item === FormConstants.REPORTS_SCREENS.REPORTS
                  ? inputName !==
                      FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM &&
                    !optionsList.length
                    ? true
                    : inputName !==
                          (FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM ||
                            FormConstants.INPUT_FIELD_NAMES.LEGACY_APP) &&
                        !optionsList.length
                      ? true
                      : false
                  : false
              }
            />
          </FormControlDiv>
        )}
      />
    );
  };
  const renderSelectFields = (inputName: string, item: any): ReactElement => {
    let optionsList: any = [];
    if (item.module_passed_for_dropdown && user_access_module.length > 0) {
      optionsList = user_access_module;
    } else {
      optionsList = item.static_dropdown_value
        ? item.static_dropdown_values
        : getDropdownsListByInputName(inputName) ||
          item?.drop_down_options ||
          item?.drop_down_options?.[0]?.report_type ||
          [];
    }
    return (
      // <RHFAutocomplete
      //   name={inputName}
      //   autoHighlight
      //   fullWidth
      //   label={item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''}
      //   placeholder={item.placeholder}
      //   // options={formattedDropdownValues}
      //   options={optionsList}
      //   getOptionLabel={(option: any) => option.label}
      //   renderOption={(props, option) => (
      //     <li {...props} key={option.label}>
      //       {option.label}
      //     </li>
      //   )}
      //   onChange={(event: any, newValue: any) => {
      //     const selectedValue = newValue?.label ?? '';
      //     setState(prevState => ({
      //       ...prevState,
      //       formError: ''
      //     }));
      //     handleDropdownChange(selectedValue, inputName);
      //     if (inputName === FormConstants.SUB_MODULE.SUB_MODULE) {
      //       handleSelectionList(selectedValue);
      //     }
      //   }}
      //   defaultValue={
      //     inputName === FormConstants.PURGE_TERMS.RETENTION_YEARS ||
      //     inputName === FormConstants.PURGE_TERMS.RETENTION_MONTHS
      //       ? '0'
      //       : ''
      //   }
      //   disabled={
      //     params.module_name === FormConstants.SCREENS.FINANCE &&
      //     uiFieldsData.menu_item === FormConstants.REPORTS_SCREENS.REPORTS
      //       ? inputName !== FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM &&
      //         !optionsList.length
      //         ? true
      //         : false
      //       : false
      //   }
      // />
      <Controller
        name={inputName}
        control={control}
        defaultValue={
          inputName === FormConstants.PURGE_TERMS.RETENTION_YEARS ||
          inputName === FormConstants.PURGE_TERMS.RETENTION_MONTHS
            ? '0'
            : ''
        }
        render={({field: {value, onChange}}) => (
          <FormControlDiv>
            <SelectInput
              enableDefaultOption
              options={optionsList}
              label={
                item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
              }
              placeholder={item.placeholder}
              name={inputName}
              value={value || ''}
              fullWidth
              onChange={(event: any) => {
                setAuditValue(event.target);
                onChange(event);
                setState(prevState => ({
                  ...prevState,
                  formError: ''
                }));
                handleDropdownChange(event, inputName);
                if (inputName === FormConstants.SUB_MODULE.SUB_MODULE) {
                  handleSelectionList(event);
                }
              }}
              optionValueKey={'value'}
              optionLabelKey={'value'}
              disabled={
                (params.module_name === FormConstants.SCREENS.FINANCE &&
                  uiFieldsData.menu_item ===
                    FormConstants.REPORTS_SCREENS.REPORTS) ||
                ((params.module_name === FormConstants.SCREENS.LIS ||
                  params.module_name === FormConstants.SCREENS.RIS) &&
                  uiFieldsData.name ===
                    FormConstants.GLOBAL_SEARCH.global_search_tab)
                  ? inputName !==
                      FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM &&
                    !optionsList.length
                    ? true
                    : false
                  : false
              }
            />
          </FormControlDiv>
        )}
      />
    );
  };

  const renderPrepostSelectFields = (
    inputName: string,
    item: any
  ): ReactElement => {
    const optionsList = item.static_dropdown_value
      ? item.static_dropdown_values
      : getDropdownsListByInputName(inputName) ||
        item?.drop_down_options ||
        item?.drop_down_options?.[0]?.report_type ||
        [];
    return (
      <Grid xs={6} md={5.92}>
        <div style={{display: 'flex', paddingBottom: '1.5vh'}}>
          <h4 style={{paddingRight: '1vh'}}>
            {item?.heading?.[0] ? item?.heading?.[0] || item?.heading?.[1] : ''}
          </h4>
          {uiFieldsData.label === 'Basics' && (
            <div>
              <Tooltip
                title={
                  item?.display?.[0]
                    ? item?.display?.[0] || item?.display?.[1]
                    : ''
                }>
                <InfoIcon
                  style={{width: '0.6em', height: '0.6em'}}
                  color="action"
                />
              </Tooltip>
            </div>
          )}
        </div>
        <div>
          {`${inputName}` !== 'category' && (
            <Controller
              name={inputName}
              control={control}
              defaultValue={
                inputName === FormConstants.PURGE_TERMS.RETENTION_YEARS ||
                inputName === FormConstants.PURGE_TERMS.RETENTION_MONTHS
                  ? '0'
                  : ''
              }
              render={({field: {value, onChange}}) => (
                <FormControlDiv>
                  <SelectInput
                    enableDefaultOption
                    options={optionsList}
                    label={
                      item.heading[0]
                        ? item.heading?.[0] || item.heading?.[1]
                        : ''
                    }
                    placeholder={item.placeholder}
                    name={inputName}
                    value={value || ''}
                    fullWidth
                    onChange={(event: any) => {
                      onChange(event);
                      setState(prevState => ({
                        ...prevState,
                        formError: ''
                      }));
                      handleDropdownChange(event, inputName);
                      if (inputName === FormConstants.SUB_MODULE.SUB_MODULE) {
                        handleSelectionList(event);
                      }
                    }}
                    optionValueKey={'value'}
                    optionLabelKey={'value'}
                    disabled={
                      params.module_name === FormConstants.SCREENS.FINANCE &&
                      uiFieldsData.menu_item ===
                        FormConstants.REPORTS_SCREENS.REPORTS
                        ? inputName !==
                            FormConstants.INPUT_FIELD_NAMES.SOURCE_SYSTEM &&
                          !optionsList.length
                          ? true
                          : false
                        : false
                    }
                  />
                </FormControlDiv>
              )}
            />
          )}

          {`${inputName}` === 'category' && (
            <RHFAutocomplete
              name="Category"
              placeholder="+ Category"
              multiple
              disableCloseOnSelect
              options={optionsList.map((option: any) => option.value)}
              getOptionLabel={(option: string) => option}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    {...getTagProps({index})}
                    key={option}
                    label={option}
                    size="small"
                    color="info"
                    variant="soft"
                  />
                ))
              }
            />
          )}
        </div>
      </Grid>
    );
  };

  const handleOnChangeCheckbox = (key: string) => {
    const sel = state.selections;
    const find = sel.indexOf(key);
    if (find > -1) {
      sel.splice(find, 1);
    } else {
      sel.push(key);
    }
    setState((prevState: FilterStateProps) => ({
      ...prevState,
      selections: sel
    }));
  };

  // const handleDisplayLabel = (uiFieldsData?: any) => {
  //   return (
  //     <>
  //     <Box>
  //       <h1>Label</h1>
  //     </Box>
  //     {renderMainFields(uiFieldsData: any)}
  //     </>
  //   )
  // }

  const renderMainFields = (uiFieldsDataObj?: any): ReactElement => {
    const uiFieldsData = uiFieldsDataObj ?? uiFields;

    const mainFieldsObjArray: any = [];

    let slice_index = 0;
    uiFieldsData.map((item: any, index: any) => {
      if (item?.value?.type === 'display_label') {
        mainFieldsObjArray.push(uiFieldsData.slice(slice_index, index), [item]);
        slice_index = index + 1;
        return;
      }
      if (index + 1 === uiFieldsData?.length) {
        const last_list_items = uiFieldsData.slice(slice_index, index + 1);
        if (last_list_items?.length) {
          mainFieldsObjArray.push(uiFieldsData.slice(slice_index, index + 1));
          return (slice_index = index);
        }
      }
    });

    return mainFieldsObjArray.map((uiFieldsArray: any, index: number) => {
      if (uiFieldsArray[0].key === 'display_label') {
        return (
          <Typography
            key={`typo-mfoa-${index}`}
            sx={{fontSize: '20px !important', mt: 2}}>
            {uiFieldsArray[0].value.heading.join(',')}
          </Typography>
        );
      }

      return (
        <>
          <Box
            rowGap={3}
            columnGap={2}
            display="grid"
            gridTemplateColumns={
              props?.gridTemplateColumns || {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(3, 1fr)'
              }
            }>
            {uiFieldsArray?.map((item: any, index: number) => {
              const key = `${item?.key}-${index}`;
              return (
                <Box key={key}>
                  {item?.value?.type === FormConstants.FIELD_TYPES.DATE
                    ? renderDateField(item?.key, item?.value)
                    : item?.value?.type === 'year'
                      ? renderYearField(item?.key, item?.value)
                      : item?.value?.type === FormConstants.FIELD_TYPES.LIST ||
                          item?.value?.type ===
                            FormConstants.FIELD_TYPES.ENUM ||
                          item?.value?.type === FormConstants.FIELD_TYPES.SELECT
                        ? renderSelectFields(item?.key, item?.value)
                        : item?.value?.type ===
                            FormConstants.FIELD_TYPES.MULTI_SELECT
                          ? renderSelectFieldsMulti(item?.key, item?.value)
                          : item?.value?.type ===
                              FormConstants.FIELD_TYPES.MULTI_SELECT_SEARCH
                            ? renderSelectFieldsMultiSearchable(
                                item?.key,
                                item?.value
                              )
                            : // : item?.value?.type === 'display_label'
                              //   ? renderDisplayLabel(item?.key, item?.value)
                              item?.value?.type ===
                                FormConstants.FIELD_TYPES.RADIO
                              ? renderRadioButtonField(item?.key, item?.value)
                              : item?.value?.type ===
                                  FormConstants.FIELD_TYPES.CHECKBOX
                                ? renderCheckBoxButtonField(
                                    item?.key,
                                    item?.value
                                  )
                                : renderTextField(item?.key, item?.value)}
                </Box>
              );
            })}
          </Box>

          {selectionListLoader ? (
            <CircularProgressDiv height="200px">
              <PulseLoader color={theme.palette.primary.main} />
            </CircularProgressDiv>
          ) : selectionListData && selectionListData.length ? (
            <div>
              <SelectionListDiv>
                <div style={{margin: 0}}>
                  {t('PRIVATE.PURGE.SELECTION_LIST', {ns: 'private'})}
                </div>
                {Object.keys(selectionListData).length
                  ? selectionListData.map((item: any, index: number) => {
                      let LabelName: any = Object.entries(item);
                      LabelName = LabelName[0][1];
                      return (
                        <div key={index}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                onChange={() => handleOnChangeCheckbox(item)}
                              />
                            }
                            label={LabelName}
                          />
                        </div>
                      );
                    })
                  : null}
              </SelectionListDiv>
            </div>
          ) : null}
        </>
      );
    });
  };
  const renderPrepostBasicLearningField = (): ReactElement => {
    return (
      <>
        <Box>
          <Grid
            container
            spacing={5}
            sx={{
              marginTop: '0 !important',
              marginLeft: '0 !important',
              rowGap: '2vh',
              columnGap: '1vh',
              width: '100% !important'
            }}>
            {uiFields?.map((item: any, index: number) => {
              return item?.value?.type === FormConstants.FIELD_TYPES.DATE
                ? renderDateField(item?.key, item?.value)
                : item?.value?.type === FormConstants.FIELD_TYPES.LIST ||
                    item?.value?.type === FormConstants.FIELD_TYPES.ENUM ||
                    item?.value?.type === FormConstants.FIELD_TYPES.SELECT
                  ? renderPrepostSelectFields(item?.key, item?.value)
                  : renderPrepostTextfield(item?.key, item?.value);
            })}
          </Grid>
        </Box>

        {selectionListLoader ? (
          <CircularProgressDiv height="200px">
            <PulseLoader color={theme.palette.primary.main} />
          </CircularProgressDiv>
        ) : selectionListData && selectionListData.length ? (
          <div>
            <SelectionListDiv>
              <div style={{margin: 0}}>
                {t('PRIVATE.PURGE.SELECTION_LIST', {ns: 'private'})}
              </div>
              {Object.keys(selectionListData).length
                ? selectionListData.map((item: any, index: number) => {
                    let LabelName: any = Object.entries(item);
                    LabelName = LabelName[0][1];
                    return (
                      <div key={index}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={() => handleOnChangeCheckbox(item)}
                            />
                          }
                          label={LabelName}
                        />
                      </div>
                    );
                  })
                : null}
            </SelectionListDiv>
          </div>
        ) : null}
      </>
    );
  };
  const renderPrepostLearningField = (): ReactElement => {
    return (
      <>
        <Box>
          <Grid
            container
            xs={12}
            sx={{display: 'flex', justifyContent: 'space-between', pb: '3vh'}}>
            <Typography variant="h2" gutterBottom>
              My Courses
            </Typography>
            <div style={{paddingRight: '5vh'}}>
              <Fab
                color="primary"
                aria-label="add"
                size="small"
                onClick={() => handleaddnewCourse()}>
                <AddIcon />
              </Fab>
            </div>
          </Grid>

          <Grid
            rowGap={3}
            columnGap={2}
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(3, 1fr)'
            }}>
            {uiFields?.map((item: any, index: number) => {
              return item?.value?.type === FormConstants.FIELD_TYPES.DATE
                ? renderDateField(item?.key, item?.value)
                : item?.value?.type === FormConstants.FIELD_TYPES.LIST ||
                    item?.value?.type === FormConstants.FIELD_TYPES.ENUM ||
                    item?.value?.type === FormConstants.FIELD_TYPES.SELECT
                  ? renderPrepostSelectFields(item?.key, item?.value)
                  : renderPrepostTextfield(item?.key, item?.value);
            })}
          </Grid>
        </Box>

        {selectionListLoader ? (
          <CircularProgressDiv height="200px">
            <PulseLoader color={theme.palette.primary.main} />
          </CircularProgressDiv>
        ) : selectionListData && selectionListData.length ? (
          <div>
            <SelectionListDiv>
              <div style={{margin: 0}}>
                {t('PRIVATE.PURGE.SELECTION_LIST', {ns: 'private'})}
              </div>
              {Object.keys(selectionListData).length
                ? selectionListData.map((item: any, index: number) => {
                    let LabelName: any = Object.entries(item);
                    LabelName = LabelName[0][1];
                    return (
                      <div key={index}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              onChange={() => handleOnChangeCheckbox(item)}
                            />
                          }
                          label={LabelName}
                        />
                      </div>
                    );
                  })
                : null}
            </SelectionListDiv>
          </div>
        ) : null}
      </>
    );
  };

  const renderSearchButton = () => {
    return (
      <LoadingButton
        type="submit"
        variant="contained"
        loading={isComponentLoading}
        sx={{
          // mr: 2,
          backgroundColor: theme.palette.primary.dark
        }}
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.preventDefault();
          handleSubmit(onSubmit)();
          if (setExpanded) {
            setExpanded();
          }
        }}>
        {uiFieldsData.sub_module_name ===
        FormConstants.REPORTS_SCREENS.REPORTS ? (
          t('PRIVATE.COMMON.EXPORT', {ns: 'private'})
        ) : uiFieldsData.module_name ===
            FormConstants.MODULE.PURGE_AND_LEGAL_HOLD ||
          uiFieldsData.module_name === FormConstants.MODULE.EXTERNAL_AUDIT ||
          menuItem === FormConstants.MENU_ITEM.USER_ACCESS ? (
          searchLoader ? (
            <PulseLoader size={8} color={theme.palette.primary.main} />
          ) : (
            t('PRIVATE.COMMON.SAVE', {ns: 'private'})
          )
        ) : (
          t('PRIVATE.COMMON.SEARCH', {ns: 'private'})
        )}
      </LoadingButton>
    );
  };

  const renderDefaultDateButton = () => {
    // const sixMonthsAgo = dayjs().subtract(6, 'months');
    // return (
    //   <LoadingButton
    //     type="reset"
    //     variant="outlined"
    //     onClick={() => {
    //       {
    //         setValue('from_date', sixMonthsAgo);
    //         setValue('to_date', dayjs());
    //         props?.setParentState((prevState: any) => {
    //           return {
    //             ...prevState,
    //             from_date: sixMonthsAgo,
    //             to_date: new Date()
    //           };
    //         });
    //       }
    //     }}
    //     loading={isSubmitting}
    //     disabled={searchLoader ? true : false}
    //     sx={{
    //       mr: 2,
    //       backgroundColor: theme.palette.primary.main
    //     }}>
    //     Default Date Range
    //   </LoadingButton>
    // );

    return (
      <DateRangeSelector
        theme={theme}
        isSubmitting={isSubmitting}
        setParentState={props?.setParentState}
        searchLoader={searchLoader}
      />
    );
  };

  const renderResetButton = () => {
    return (
      <LoadingButton
        type="reset"
        variant="outlined"
        onClick={onReset}
        loading={isSubmitting}
        disabled={searchLoader ? true : false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.light
        }}>
        {uiFieldsData.sub_module_name === FormConstants.REPORTS_SCREENS.REPORTS
          ? t('PRIVATE.COMMON.CLEAR', {ns: 'private'})
          : uiFieldsData.module_name ===
                FormConstants.MODULE.PURGE_AND_LEGAL_HOLD ||
              uiFieldsData.module_name ===
                FormConstants.MODULE.EXTERNAL_AUDIT ||
              menuItem === FormConstants.MENU_ITEM.USER_ACCESS
            ? t('PRIVATE.COMMON.CLEAR', {ns: 'private'})
            : t('PRIVATE.COMMON.RESET', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderClearButton = () => {
    return (
      <LoadingButton
        type="reset"
        variant="outlined"
        onClick={onReset}
        loading={isSubmitting}
        disabled={searchLoader ? true : false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.light
        }}>
        {t('PRIVATE.COMMON.CLEAR', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderImportPatientListButton = () => {
    return (
      <LoadingButton
        type="button"
        variant="contained"
        onClick={() => {
          if (handleImportButton && typeof handleImportButton === 'function') {
            handleImportButton();
          }
        }}
        loading={isSubmitting}
        disabled={searchLoader ? true : false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.dark
        }}>
        {t('PRIVATE.COMMON.IMPORT_PATIENT_LIST', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderDownloadTemplateButton = () => {
    async function downloadExcel() {
      // Function to dynamically extract dropdown options based on available fields
      const extractValues = (key: any) => {
        const entry = dropDownValues.find(item => item[key]);
        return entry ? entry[key].map((obj: any) => obj.value) : [];
      };

      // Step 1: Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');

      // Step 2: Extract headers dynamically as the keys of uiFieldsData.fields
      const fieldEntries = Object.keys(uiFieldsData.fields).sort(
        (a, b) => uiFieldsData.fields[a].order - uiFieldsData.fields[b].order
      ); // Sort fields by order

      worksheet.addRow(fieldEntries); // Add headers to Excel

      // Step 3: Set column widths for better visibility
      worksheet.columns = fieldEntries.map(() => ({width: 25}));

      // Step 4: Apply dropdowns dynamically for all fields that have predefined options
      const applyDropdown = (columnLetter: string, options: string[]): void => {
        (worksheet as any).dataValidations.add(
          `${columnLetter}2:${columnLetter}99999`,
          {
            type: 'list',
            allowBlank: false,
            formulae: [`"${options.join(',')}"`]
          }
        );
      };

      // Step 5: Iterate through all input fields and check if they have dropdown values
      Object.keys(uiFieldsData.fields).forEach((fieldKey, index) => {
        const options = extractValues(fieldKey); // Extract dropdown options dynamically
        if (options.length > 0) {
          const columnLetter = String.fromCharCode(65 + index); // Convert index to Excel column letter (A, B, C...)
          applyDropdown(columnLetter, options);
        }
      });

      // Step 6: Format the header row
      const headerRow = worksheet.getRow(1);
      headerRow.eachCell(cell => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {argb: 'FFADD8E6'} // Light Blue Header
        };
        cell.font = {name: 'Inter', size: 10, bold: true};
        cell.alignment = {horizontal: 'center'};
      });

      // Step 7: Auto-format all rows for readability
      worksheet.eachRow(row => {
        row.eachCell(cell => {
          cell.font = {name: 'Inter', size: 9};
          cell.alignment = {horizontal: 'center'};
        });
      });

      // Step 8: Generate and download the Excel file
      const excelBlob = await workbook.xlsx.writeBuffer();
      const blob = new Blob([excelBlob], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'audit_template.xlsx';

      document.body.appendChild(link);
      link.click();

      // Cleanup
      URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
    }

    return (
      <LoadingButton
        type="button"
        variant="contained"
        onClick={() => {
          downloadExcel();
        }}
        loading={isSubmitting}
        disabled={searchLoader ? true : false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.dark
        }}>
        {t('PRIVATE.COMMON.DOWNLOAD_TEMPLATE', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderCancelButton = () => {
    return (
      <LoadingButton
        type="button"
        variant="outlined"
        onClick={() => {
          if (handleCancelButton && typeof handleCancelButton === 'function') {
            handleCancelButton();
          }
        }}
        loading={isSubmitting}
        disabled={searchLoader ? true : false}
        sx={{
          mr: 2,
          backgroundColor: theme.palette.primary.light
        }}>
        {t('PRIVATE.ROLES.CANCEL', {ns: 'private'})}
      </LoadingButton>
    );
  };

  const renderFormContainer = (): ReactElement => {
    return (
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        {!dropdownLoader ? (
          renderMainFields()
        ) : (
          <CircularProgressDiv height="200px">
            <PulseLoader
              style={{
                marginTop: '17px'
              }}
              size={12}
              color={theme.palette.primary.main}
            />
          </CircularProgressDiv>
        )}
        {uiFieldsData?.filter_builder && (
          <FilterBuilderView
            setFilters={setFilters}
            filters={filters}
            config={uiFieldsData.filter_builder}
            onChange={filterValues => {
              setFilterBuilderData({filters: filterValues});
            }}
          />
        )}

        {!dropdownLoader &&
          uiFieldsData.name != FormConstants.STATIC_VALUES.PATIENT_LOG &&
          uiFieldsData.name != FormConstants.STATIC_VALUES.ACCESSLOG && (
            <Stack spacing={3} alignItems="flex-end" sx={{mt: 2}}>
              <Grid item xs={12} display="flex" sm={6} md={12}>
                {uiFieldsData.name === 'rcm-dashboard' &&
                params.module_name === 'patient-accounting'
                  ? renderDefaultDateButton()
                  : null}
                {uiFieldsData.sub_module_name ===
                  FormConstants.REPORTS_SCREENS.REPORTS ||
                  uiFieldsData.module_name ===
                    FormConstants.MODULE.PURGE_AND_LEGAL_HOLD ||
                  uiFieldsData.module_name ===
                    FormConstants.MODULE.EXTERNAL_AUDIT ||
                  (menuItem === FormConstants.MENU_ITEM.USER_ACCESS &&
                    renderClearButton())}
                {params.module_name !== 'course_management'
                  ? uiFieldsData.module_name ===
                      FormConstants.MODULE.PURGE_AND_LEGAL_HOLD ||
                    uiFieldsData.module_name ===
                      FormConstants.MODULE.EXTERNAL_AUDIT ||
                    menuItem === FormConstants.MENU_ITEM.USER_ACCESS
                    ? renderCancelButton()
                    : renderResetButton()
                  : ''}
                {uiFieldsData.module_name ===
                FormConstants.MODULE.EXTERNAL_AUDIT
                  ? renderImportPatientListButton()
                  : null}
                {uiFieldsData.module_name ===
                FormConstants.MODULE.EXTERNAL_AUDIT
                  ? renderDownloadTemplateButton()
                  : null}
                {uiFieldsData.fields && renderSearchButton()}
              </Grid>
            </Stack>
          )}
      </FormProvider>
    );
  };

  return (
    <>
      {(uiFieldsData.name === FormConstants.STATIC_VALUES.PATIENT_LOG &&
        userForm) ||
      (uiFieldsData.name === FormConstants.STATIC_VALUES.PATIENT_LOG &&
        patientForm) ||
      (uiFieldsData.name === FormConstants.STATIC_VALUES.ACCESSLOG &&
        accessForm) ? (
        <Stack mt={1}>{renderFormContainer()}</Stack>
      ) : (
        <Grid>
          <Card sx={{p: 3, boxShadow: 5, borderRadius: '8px'}}>
            {renderFormContainer()}
            <div style={{color: 'red', marginBottom: '1rem'}}>{formError}</div>
          </Card>
        </Grid>
      )}

      <Box>
        {uiFieldsData.name === FormConstants.STATIC_VALUES.PATIENT_LOG &&
        userForm ? (
          <ModuleSearchPage
            uiFieldsData={uiFieldsData?.user}
            menuItem={menuItem}
            module={props?.module}
            interface={true}
            // hasFields={true}
          />
        ) : uiFieldsData.name === FormConstants.STATIC_VALUES.PATIENT_LOG &&
          patientForm ? (
          <ModuleSearchPage
            uiFieldsData={uiFieldsData?.patient}
            menuItem={menuItem}
            module={props?.module}
            interface={true}
            // hasFields={true}
          />
        ) : uiFieldsData.name === FormConstants.STATIC_VALUES.ACCESSLOG &&
          accessForm ? (
          <ModuleSearchPage
            selectedModule={state?.moduleAcessSelected}
            uiFieldsData={uiFieldsData?.[moduleAcessSelected]}
            menuItem={menuItem}
            module={props?.module}
            interface={true}
            // hasFields={true}
          />
        ) : (
          ''
        )}
      </Box>
    </>
  );
};

export default ModuleSearchFilter;
